import type {
  MarketAreaModel,
  MarketEquipmentModel,
  MarketFieldProvenance,
  MarketListing,
  MarketLocationHierarchy,
  MarketSubjectSnapshot,
  MarketTechnicalModel
} from "../domain/types.ts";

export const PUBLIC_MARKET_SOURCE_IDS = ["nehnutelnosti-sk", "reality-sk", "bazos-sk", "topreality-sk"] as const;
export type PublicMarketSourceId = (typeof PUBLIC_MARKET_SOURCE_IDS)[number] | string;

const UNITED_CLASSIFIEDS_PROVIDER_IDS = new Set(["nehnutelnosti-sk", "reality-sk"]);

export const publicMarketIdentityNamespace = (providerId: string) =>
  UNITED_CLASSIFIEDS_PROVIDER_IDS.has(providerId.trim().toLocaleLowerCase("sk"))
    ? "UNITED_CLASSIFIEDS"
    : `PROVIDER:${providerId.trim().toLocaleLowerCase("sk")}`;

export type PublicMarketQuery = {
  city: string;
  municipalityId?: string;
  propertyType: string;
  rooms?: number;
  roomVariants: string[];
  transactionType?: MarketSubjectSnapshot["transactionType"];
  area?: number;
  locality?: string;
  street?: string;
  capturedAt: string;
};

export type RawPublicMarketListing = {
  providerId: PublicMarketSourceId;
  sourceName: string;
  sourceUrl: string;
  externalId?: string;
  title?: string;
  city?: string;
  street?: string;
  locality?: string;
  propertyType?: string;
  rooms?: string | number;
  area?: string | number;
  askingPrice?: string | number;
  pricePerM2?: string | number;
  condition?: string;
  electricity?: string;
  coreType?: string;
  kitchen?: string;
  floor?: string | number;
  totalFloors?: string | number;
  elevator?: string | boolean;
  balconyType?: string;
  cellar?: string | boolean;
  orientation?: string;
  buildingType?: string;
  reconstruction?: string;
  publishedAt?: string;
  capturedAt?: string;
  status?: string;
  description?: string;
  agency?: string;
  municipalityId?: string;
  location?: MarketLocationHierarchy;
  areas?: MarketAreaModel;
  technical?: MarketTechnicalModel;
  equipment?: MarketEquipmentModel;
  fieldProvenance?: MarketFieldProvenance;
};

export type PublicMarketSourceStatus = {
  providerId: PublicMarketSourceId;
  sourceName: string;
  status: "ready" | "unavailable" | "failed";
  rawCount: number;
  preliminaryCount?: number;
  detailCount?: number;
  requestCount?: number;
  cacheStatus?: "miss" | "hit" | "stale";
  cacheAgeMinutes?: number;
  message?: string;
  capturedAt: string;
};

export type PublicMarketSourceResult = {
  listings: RawPublicMarketListing[];
  status: PublicMarketSourceStatus;
};

export interface PublicMarketSourceAdapter {
  readonly id: PublicMarketSourceId;
  readonly name: string;
  readonly available: boolean;
  discover(query: PublicMarketQuery): Promise<PublicMarketSourceResult>;
}

export type PublicMarketDiscoveryReport = {
  capturedAt: string;
  rawCount: number;
  normalizedCount: number;
  uniqueCount: number;
  preliminaryCount: number;
  detailCount: number;
  requestCount: number;
  sourceStatuses: PublicMarketSourceStatus[];
};

export type CanonicalPublicMarketListing = MarketListing & {
  city: string;
  propertyType: string;
  rooms: number;
};
